import _ from "lodash";
import { useEffect, useState } from "react";
import { formatCurrency } from "../../utils/CurrencyUtils";
import { getWithToken, postWithToken } from "../../utils/Api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Loader2 } from "lucide-react";

export default function TransactionScreen() {
    const [transactions, setTransactions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState("");
    const [isProcessing, setIsProcessing] = useState({}); // State untuk loading per transaksi
    const [showModal, setShowModal] = useState(false); // State untuk menampilkan modal
    const [selectedTransactionId, setSelectedTransactionId] = useState(null); // State untuk menyimpan transaction_id yang dipilih
    const [expandedTransactions, setExpandedTransactions] = useState({}); // State untuk menyimpan card yang diperluas
    const [statusChangeLoading, setStatusChangeLoading] = useState({}); // State untuk loading status change
    const [showUpdateTujuan, setShowUpdateTujuan] = useState({});
    const [newTujuan, setNewTujuan] = useState({});
    const [statusFilter, setStatusFilter] = useState(""); // State untuk filter status

    const statusOptions = [
        { value: "all", label: "Semua Status" },
        { value: "0", label: "Belum Dibayar" },
        { value: "1", label: "Pembayaran Gagal" },
        { value: "2", label: "Expired" },
        { value: "3", label: "Pending" },
        { value: "5", label: "Gagal" },
        { value: "6", label: "Berhasil" },
        { value: "7", label: "Diisi Manual" },
        { value: "8", label: "Refund" },
    ];

    function generatePaymentMsg(
        status,
        phoneNumber,
        productName,
        productPrice,
        createdAt
    ) {
        switch (status) {
            case "5":
                return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
                    `Terima kasih telah menggunakan layanan Millipoint.id untuk melakukan topup item. \n\n` +
                    `Mohon maaf, saat ini sistem kami sedang mengalami gangguan, sehingga transaksi gagal.\n` +
                    `Untuk menindak lanjuti hal tersebut, admin akan melakukan pengisian item secara manual, jadi mohon tunggu.\n\n` +
                    `Salam hormat, Millipoint.\n` +
                    `Topup murah dan terpercaya anda`
                )}`;
            default:
                return `https://wa.me/${phoneNumber}`;
        }
    }

    const mapStatus = (status) => {
        switch (status) {
            case "0":
                return { text: "Menunggu Pembayaran", color: "bg-yellow-600/20 text-yellow-500 border-yellow-500/30" };
            case "2":
                return { text: "Expired", color: "bg-zinc-800/80 text-zinc-400 border-zinc-700/50" };
            case "3":
                return { text: "Pending", color: "bg-blue-600/20 text-blue-400 border-blue-500/30" };
            case "6":
                return { text: "Sukses", color: "bg-green-600/20 text-green-400 border-green-500/30" };
            case "7":
                return { text: "Diisi Manual", color: "bg-purple-600/20 text-purple-400 border-purple-500/30" };
            case "8":
                return { text: "Refund", color: "bg-orange-600/20 text-orange-400 border-orange-500/30" };
            default:
                return { text: "Gagal", color: "bg-red-600/20 text-red-400 border-red-500/30" };
        }
    };

    useEffect(() => {
        fetchData(currentPage, search, statusFilter);
    }, [currentPage, search, statusFilter]);

    function fetchData(page, search, status = "") {
        let url = `/get-all-transactions?page=${page}&search=${search}`;

        if (status !== "") {
            url += `&status=${status}`;
        }

        getWithToken(url)
            .then((res) => {
                setTransactions(res?.data);
                setTotalPages(res?.pagination?.total_page);
            })
            .catch((e) => {
                console.error("Failed to fetch transactions", e);
            });
    }

    async function processTransaction(transaction_id, newTujuan = null) {
        setIsProcessing((prev) => ({ ...prev, [transaction_id]: true }));
        try {
            const payload = {
                transaction_id: transaction_id,
                ...(newTujuan && { tujuan: newTujuan })
            };
            await postWithToken(`/redo-transaction`, payload);
            fetchData(currentPage, search, statusFilter);
        } catch (e) {
            console.error("Failed to process transaction", e);
        } finally {
            setIsProcessing((prev) => ({ ...prev, [transaction_id]: false }));
            setShowUpdateTujuan((prev) => ({ ...prev, [transaction_id]: false }));
            setNewTujuan((prev) => ({ ...prev, [transaction_id]: "" }));
        }
    }

    async function changeTransactionStatus(transactionId, newStatus) {
        setStatusChangeLoading((prev) => ({ ...prev, [transactionId]: true }));
        try {
            await postWithToken(`/change-transaction-status`, {
                id: transactionId,
                status: newStatus
            });
            fetchData(currentPage, search, statusFilter);
        } catch (e) {
            console.error("Failed to change transaction status", e);
        } finally {
            setStatusChangeLoading((prev) => ({ ...prev, [transactionId]: false }));
        }
    }

    const handleSearch = _.debounce((value) => {
        setSearch(value);
        setCurrentPage(1);
    }, 300);

    const handleProcessClick = (transaction_id) => {
        setSelectedTransactionId(transaction_id);
        setShowModal(true);
    };

    const handleContinue = () => {
        if (selectedTransactionId) {
            const tujuan = newTujuan[selectedTransactionId] || null;
            processTransaction(selectedTransactionId, tujuan);
            setShowModal(false);
        }
    };

    const toggleExpand = (transaction_id) => {
        setExpandedTransactions((prev) => ({
            ...prev,
            [transaction_id]: !prev[transaction_id],
        }));
    };

    function formatDateTime(isoString) {
        const date = new Date(isoString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hour = String(date.getHours()).padStart(2, '0');
        const minute = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hour}:${minute}`;
    }

    const handleStatusChange = (transactionId, newStatus) => {
        if (newStatus === "7" || newStatus === "8") {
            changeTransactionStatus(transactionId, newStatus);
        }
    };

    const toggleUpdateTujuan = (transactionId) => {
        setShowUpdateTujuan((prev) => ({
            ...prev,
            [transactionId]: !prev[transactionId]
        }));
        if (!showUpdateTujuan[transactionId]) {
            setNewTujuan((prev) => ({
                ...prev,
                [transactionId]: transactions.find(t => t.id === transactionId)?.tujuan || ""
            }));
        }
    };

    return (
        <div className="min-h-screen bg-black text-white p-6">
            <h1 className="text-3xl font-bold mb-6 text-red-600">Daftar Transaksi</h1>
            <div className="mb-6 flex flex-col md:flex-row gap-4">
                <div className="flex-1 w-full">
                    <Input
                        type="text"
                        placeholder="Search transactions..."
                        defaultValue={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="w-full bg-zinc-900 border-zinc-800 text-white placeholder-zinc-500 focus-visible:ring-red-600"
                    />
                </div>
                <div className="w-full md:w-1/4">
                    <Select
                        value={statusFilter || "all"}
                        onValueChange={(val) => {
                            setStatusFilter(val === "all" ? "" : val);
                            setCurrentPage(1);
                        }}
                    >
                        <SelectTrigger className="w-full bg-zinc-900 border-zinc-800 text-white focus:ring-red-600">
                            <SelectValue placeholder="Semua Status" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-800 text-white">
                            {statusOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="space-y-4 mt-6">
                {transactions?.map((transaction) => {
                    const statusInfo = mapStatus(transaction.status);
                    return (
                        <Card
                            key={transaction.id}
                            className="bg-zinc-950 border-zinc-800 text-white w-full"
                        >
                            <CardHeader className="flex flex-row justify-between items-start pb-2">
                                <div>
                                    <CardTitle className="text-lg font-semibold text-zinc-100">
                                        Transaksi ID: {transaction.id}
                                    </CardTitle>
                                    <p className="text-xs text-zinc-500 mt-1">
                                        Ticket: {transaction.ticket_number}
                                    </p>
                                </div>
                                <Badge variant="outline" className={`${statusInfo.color} font-medium`}>
                                    {statusInfo.text}
                                </Badge>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <p className="text-sm text-zinc-400">
                                    <strong>Tanggal Transaksi:</strong> {formatDateTime(transaction.created_at)}
                                </p>
                                <div className="flex justify-end pt-2">
                                    <Button
                                        onClick={() => toggleExpand(transaction.id)}
                                        variant="secondary"
                                    >
                                        {expandedTransactions[transaction.id] ? "Tutup" : "Lihat Detail"}
                                    </Button>
                                </div>

                                {expandedTransactions[transaction.id] && (
                                    <div className="mt-4 pt-4 border-t border-zinc-800 space-y-2 text-sm text-zinc-300">
                                        <p><strong>Redo Ids:</strong> {transaction.redo_ids || "-"}</p>
                                        <p><strong>Nama Produk:</strong> {transaction.product_name || "-"}</p>
                                        <p><strong>Kode Produk:</strong> {transaction.product_sku_code || "-"}</p>
                                        <p><strong>ID Tujuan:</strong> {transaction.tujuan || "-"}</p>
                                        <p><strong>Status User:</strong> {transaction.status_message || "-"}</p>
                                        <p><strong>Status Internal:</strong> {transaction.status_internal || "-"}</p>
                                        <p><strong>Harga:</strong> {formatCurrency(transaction.final_price) || "-"}</p>
                                        <p><strong>Tanggal:</strong> {formatDateTime(transaction.created_at) || "-"}</p>
                                        <p><strong>Email:</strong> {transaction.email || "-"}</p>
                                        <p>
                                            <strong>Whatsapp:</strong>{" "}
                                            <a
                                                href={generatePaymentMsg(
                                                    transaction.status,
                                                    transaction.whatsapp,
                                                    transaction.product_name,
                                                    transaction.final_price,
                                                    transaction.created_at
                                                )}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-400 hover:text-blue-300 hover:underline"
                                            >
                                                {transaction.whatsapp}
                                            </a>
                                        </p>

                                        {transaction.status === "5" && (
                                            <div className="mt-6 pt-4 border-t border-zinc-800 space-y-4">
                                                <div className="space-y-1">
                                                    <label className="block text-xs font-semibold text-zinc-400">
                                                        Ubah Status:
                                                    </label>
                                                    <Select
                                                        value={transaction.status}
                                                        onValueChange={(val) => handleStatusChange(transaction.id, val)}
                                                        disabled={statusChangeLoading[transaction.id]}
                                                    >
                                                        <SelectTrigger className="w-full bg-zinc-900 border-zinc-800 text-white focus:ring-red-600">
                                                            <SelectValue placeholder={statusInfo.text} />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-zinc-950 border-zinc-800 text-white">
                                                            <SelectItem value={transaction.status} disabled>
                                                                {statusInfo.text}
                                                            </SelectItem>
                                                            <SelectItem value="7">Diisi Manual</SelectItem>
                                                            <SelectItem value="8">Refund</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    {statusChangeLoading[transaction.id] && (
                                                        <p className="text-xs text-yellow-500 animate-pulse mt-1">Memproses...</p>
                                                    )}
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        id={`update-tujuan-${transaction.id}`}
                                                        checked={showUpdateTujuan[transaction.id] || false}
                                                        onChange={() => toggleUpdateTujuan(transaction.id)}
                                                        className="w-4 h-4 rounded border-zinc-800 text-red-600 focus:ring-red-600 bg-zinc-900 cursor-pointer accent-red-600"
                                                    />
                                                    <label
                                                        htmlFor={`update-tujuan-${transaction.id}`}
                                                        className="text-sm text-zinc-300 cursor-pointer select-none"
                                                    >
                                                        Update Nomor Tujuan
                                                    </label>
                                                </div>

                                                {showUpdateTujuan[transaction.id] && (
                                                    <div className="space-y-1">
                                                        <Input
                                                            type="text"
                                                            value={newTujuan[transaction.id] || ""}
                                                            onChange={(e) => setNewTujuan(prev => ({
                                                                ...prev,
                                                                [transaction.id]: e.target.value
                                                            }))}
                                                            placeholder="Masukkan nomor tujuan baru"
                                                            className="w-full bg-zinc-900 border-zinc-800 text-white focus-visible:ring-red-600"
                                                        />
                                                    </div>
                                                )}

                                                <Button
                                                    onClick={() => handleProcessClick(transaction.id)}
                                                    disabled={isProcessing[transaction.id]}
                                                    variant="secondary"
                                                    className="w-full font-semibold transition-colors"
                                                >
                                                    {isProcessing[transaction.id] ? (
                                                        <>
                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                            Memproses...
                                                        </>
                                                    ) : (
                                                        "Transaksi Ulang"
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
            <div className="flex justify-center mt-6 flex-wrap gap-2">
                {Array.from({ length: totalPages }, (_, i) => (
                    <Button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        variant={currentPage === i + 1 ? "secondary" : "outline"}
                    >
                        {i + 1}
                    </Button>
                ))}
            </div>

            {/* Modal untuk Konfirmasi Transaksi Ulang */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-red-600">Konfirmasi Transaksi Ulang</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Apakah Anda yakin ingin melakukan transaksi ulang untuk Transaksi ID: <strong className="text-white">{selectedTransactionId}</strong>?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex justify-end space-x-2 mt-4">
                        <Button
                            onClick={() => setShowModal(false)}
                            variant="outline"
                            className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800"
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={handleContinue}
                            variant="secondary"
                        >
                            Konfirmasi
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
